import * as React from 'react';
import { generateId } from '../../utils/DomUtil';
import { HorizontalOriginOption, OriginOptions, VerticalOriginOption } from '../../utils/Overlay';
import { assignRef, PropsAndButtonAttrs } from '../../utils/ReactHelpers';
import { FlyoutListMenuProps, FlyoutListMenuWidget } from '../FlyoutListMenuTextWidget';
import { DropdownButton, DropdownButtonProps } from './DropdownButton';

export interface HierarchicalDropdownProps extends
  Omit<DropdownButtonProps,
    'aria-controls' | 'aria-haspopup' | 'focusOnClose' | 'getElementToFocus' | 'onClose' | 'onOpen' | 'popupContent' | 'renderPopupContent'
  >,
  Pick<FlyoutListMenuProps, 'maxHeight' | 'menuItemInfos' | 'onSelect'> {

  /** Ref to the rendered button element */
  buttonRef?: React.Ref<HTMLButtonElement>;

  /** DOM id for the popup menu */
  menuId?: string;
}

/**
 * Convenience widget which combines a `DropdownButton` widget with a `FlyoutListMenuWidget`, with proper a11y.
 */
export const HierarchicalDropdownWidget = React.forwardRef<DropdownButton, PropsAndButtonAttrs<HierarchicalDropdownProps>>((props, ref) => {
  // eslint-disable-next-line deprecation/deprecation
  const { buttonRef, menuId, menuItemInfos, maxHeight, onSelect, popupAlignment, stackingRootId, zIndex, ...dropdownProps } = props;
  const [isOpen, setOpen] = React.useState(false);
  const DefaultMenuId = React.useState(generateId())[0];
  const closeFn = React.useRef<(() => void) | null>(null);
  const buttonElement = React.useRef<HTMLButtonElement | null>(null);

  const anchorOrigin: OriginOptions = {
    horizontal: popupAlignment === 'left' ? HorizontalOriginOption.left : HorizontalOriginOption.right,
    vertical: VerticalOriginOption.bottom,
  };

  const stackedOrigin: OriginOptions = {
    horizontal: anchorOrigin.horizontal,
    vertical: VerticalOriginOption.top,
  };

  const menuProps: FlyoutListMenuProps = {
    allowAutoPosition: true,
    anchorEl: buttonElement.current,
    anchorOrigin,
    id: menuId || DefaultMenuId,
    maxHeight,
    menuItemInfos,
    menuStyle: { ...(isOpen && { minWidth: buttonElement.current!.getBoundingClientRect().width }) },
    onSelect: (value: string) => { onSelect(value); closeFn.current!(); },
    requestCloseAll: closeFn.current!,
    stackedOrigin,
    stackingRootId,
    testId: props.testId!,
    toggleEl: buttonElement.current,
    zIndex,
  };

  return <>
    <DropdownButton
      aria-controls={menuProps.id!}
      aria-haspopup='menu'
      {...dropdownProps}
      buttonRef={buttonElem => {
        assignRef(props.buttonRef, buttonElem);
        buttonElement.current = buttonElem;
      }}
      children={props.children || ''}
      focusOnClose
      onClose={() => { setOpen(false); closeFn.current = null; }}
      onOpen={doneFn => { closeFn.current = doneFn; setOpen(true); }}
      ref={ref}
    />
    {!props.disabled && isOpen && <FlyoutListMenuWidget {...menuProps} />}
  </>;
});
HierarchicalDropdownWidget.displayName = 'HierarchicalDropdownWidget';
